/**
 * The bulk-shift toolbar (spec 5.3): relative-month steppers (negative = earlier) that
 * shift the selected points, plus the {@link BulkShiftResult} summary ("Applied 214 —
 * skipped 3 reported, 1 out of order") with a drill-in list of the skipped points and
 * their reasons. Eligibility is computed by the parent's edit layer; this component is
 * pure presentation over the count + result.
 */

import { useMemo, useState } from 'react';
import { Button, NumberInput } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { MILESTONE_LABELS } from './constants';
import type { BulkShiftResult } from './types';
import styles from './bulk-shift-toolbar.module.scss';

export interface BulkShiftToolbarProps {
  selectedCount: number;
  onApply: (offsetMonths: number) => void;
  onClear: () => void;
  lastResult: BulkShiftResult | null;
}

/**
 * Renders the selected-point count, the relative-month stepper + Apply, a Clear
 * action, and the last bulk-shift result with a skipped-points drill-in.
 *
 * @param {BulkShiftToolbarProps} props - The selection count, apply/clear handlers, and last result.
 * @return {JSX.Element} The toolbar.
 */
export function BulkShiftToolbar({
  selectedCount,
  onApply,
  onClear,
  lastResult,
}: BulkShiftToolbarProps) {
  const [offset, setOffset] = useState(0);
  const [showSkipped, setShowSkipped] = useState(false);

  const reportedCount = useMemo(
    () =>
      lastResult?.skipped.filter((s) => s.reason === 'reported').length ?? 0,
    [lastResult]
  );
  const outOfOrderCount = useMemo(
    () =>
      lastResult?.skipped.filter((s) => s.reason === 'outOfOrder').length ?? 0,
    [lastResult]
  );

  const hasSelection = selectedCount > 0;

  return (
    <div className={styles.toolbar}>
      <span className={styles.count} aria-live="polite">
        <FormattedCount value={selectedCount} /> selected
      </span>

      <NumberInput
        id="bulk-shift-months"
        className={styles.months}
        label="Shift (months)"
        hideLabel
        min={-120}
        max={120}
        step={1}
        value={offset}
        disabled={!hasSelection}
        onChange={(_event, { value }) => setOffset(Number(value) || 0)}
      />
      <Button
        size="sm"
        kind="primary"
        disabled={!hasSelection || offset === 0}
        onClick={() => onApply(offset)}
      >
        Apply shift
      </Button>
      <Button size="sm" kind="ghost" disabled={!hasSelection} onClick={onClear}>
        Clear
      </Button>

      {lastResult && (
        <div className={styles.result} aria-live="polite">
          <span className={styles.summary}>
            Applied <FormattedCount value={lastResult.appliedCount} />
            {lastResult.skipped.length > 0 && (
              <>
                {' — skipped '}
                <FormattedCount value={reportedCount} /> reported,{' '}
                <FormattedCount value={outOfOrderCount} /> out of order
              </>
            )}
          </span>
          {lastResult.skipped.length > 0 && (
            <Button
              size="sm"
              kind="ghost"
              onClick={() => setShowSkipped((prev) => !prev)}
            >
              {showSkipped ? 'Hide details' : 'Show details'}
            </Button>
          )}
          {showSkipped && (
            <ul className={styles.skippedList}>
              {lastResult.skipped.map((point) => (
                <li key={`${point.slotId}|${point.field}`}>
                  {point.slotId} · {MILESTONE_LABELS[point.field]} —{' '}
                  {point.reason === 'reported' ? 'reported' : 'out of order'}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
